import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Download, Shield, CheckCircle } from 'lucide-react';
import { digitalProductService } from '@/services/digitalProduct.service';
import { enrollmentService } from '@/services/enrollment.service';
import { stashedInfoService } from '@/services/stashedInfo.service';
import { authService } from '@/services/auth.service';
import { errorReportingService } from '@/services/errorReporting.service';
import { discountService, DiscountValidationResponse } from '@/services/discount.service';
import DiscountCodeInput from '@/components/enrollment/DiscountCodeInput';
import userActions from '@/redux/actions/user';
import { clearEnrollmentProduct } from '@/redux/actions/enrollment';
import { trackEnrollment, trackForm, trackError, trackPageView } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';
import { metaPixel } from '@/services/metaPixel';

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '')
  .catch((error) => {
    console.error('Failed to load Stripe.js:', error);
    errorReportingService.reportError({
      error_type: 'StripeLoadError',
      error_message: error?.message || 'Failed to load Stripe.js',
      additional_context: {
        'Error Source': 'Digital Product Checkout',
        'Likely Cause': 'Ad blocker preventing Stripe.js from loading'
      }
    });
    return null;
  });

interface CheckoutFormData {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#e5e7eb',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      lineHeight: '24px',
      '::placeholder': {
        color: '#9ca3af',
      },
      iconColor: '#9ca3af',
    },
    invalid: {
      color: '#f87171',
      iconColor: '#f87171',
    },
  },
  hidePostalCode: false,
};

const formatPrice = (price: number) => `$${price.toFixed(2)}`;

const DigitalProductCheckoutForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();

  const { productId, productType } = useSelector((state: any) => state.enrollment);

  const [productInfo, setProductInfo] = useState<any>(null);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [productError, setProductError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CheckoutFormData>({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CheckoutFormData | 'card' | 'terms', string>>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameCheckTimeout, setUsernameCheckTimeout] = useState<NodeJS.Timeout | null>(null);

  const [enrollmentMode, setEnrollmentMode] = useState<'new' | 'existing' | 'authenticated'>('new');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string>('');
  const [formStarted, setFormStarted] = useState(false);

  const [appliedDiscount, setAppliedDiscount] = useState<DiscountValidationResponse | null>(null);
  const [appliedDiscountCode, setAppliedDiscountCode] = useState<string>('');

  // Terms acceptance state
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useScrollTracking('DigitalProductCheckout');

  // Fetch product information
  useEffect(() => {
    const fetchProductInfo = async () => {
      if (!productId || productType !== 'digital_product') {
        setProductError('No digital product selected');
        setLoadingProduct(false);
        return;
      }

      trackPageView('DigitalProductCheckout', { productId, productType });
    
      // Meta Pixel - Initialize and track page view
      metaPixel.init();
      metaPixel.pageView();

      try {
        setLoadingProduct(true);
        setProductError(null);

        const response = await digitalProductService.getProductBySlug(productId);
        if (response.success && response.data) {
          setProductInfo(response.data);
        } else {
          setProductError(response.error || 'Failed to load product information');
        }
      } catch (error) {
        console.error('Error fetching product info:', error);
        setProductError('An error occurred while loading product information');
      } finally {
        setLoadingProduct(false);
      }
    };

    fetchProductInfo();
  }, [productId, productType]);

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userStr = localStorage.getItem('user');
    let user = authService.getCurrentUser();

    if (!user && userStr && userStr !== 'undefined' && userStr !== 'null') {
      try {
        const parsedUser = JSON.parse(userStr);
        if (parsedUser && typeof parsedUser === 'object' && parsedUser.email) {
          user = parsedUser;
        }
      } catch (e) {}
    }

    const hasValidAuth = token && token !== 'undefined' && token !== 'null' && user && user.email;

    if (hasValidAuth) {
      setEnrollmentMode('authenticated');
      setFormData(prev => ({
        ...prev,
        email: user.email || '',
        firstName: user.first_name || '',
        lastName: user.last_name || '',
      }));
      setInfoMessage(`Purchasing as ${user.username}`);
    }
  }, []);

  // Track field focus
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    if (!formStarted) {
      trackForm.formStart('digital-product-checkout', 'Digital Product Checkout');
      setFormStarted(true);
    }
    trackForm.fieldFocus('digital-product-checkout', name);
  };

  // Track field completion on blur
  const handleFieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value.trim()) {
      trackForm.fieldComplete('digital-product-checkout', name);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (!formStarted) {
      trackForm.formStart('digital-product-checkout', 'Digital Product Checkout');
      setFormStarted(true);
    }

    if (errors[name as keyof CheckoutFormData]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (name === 'username') {
      if (usernameCheckTimeout) {
        clearTimeout(usernameCheckTimeout);
      }
      if (value.trim().length >= 3) {
        const timeout = setTimeout(async () => {
          await checkUsernameAvailability(value.trim());
        }, 500);
        setUsernameCheckTimeout(timeout);
      }
    }
  };

  const checkUsernameAvailability = async (username: string) => {
    if (username.length < 3) return;
    setCheckingUsername(true);
    try {
      const response = await authService.checkUsernameAvailability(username);
      if (response.success && response.data) {
        if (!response.data.available) {
          setErrors(prev => ({ ...prev, username: 'Username is already taken' }));
        } else {
          setErrors(prev => ({ ...prev, username: '' }));
        }
      }
    } catch (error) {
      console.error('Error checking username:', error);
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleCardChange = (event: any) => {
    setCardComplete(event.complete);
    if (event.error) {
      setErrors(prev => ({ ...prev, card: event.error.message }));
    } else {
      setErrors(prev => ({ ...prev, card: '' }));
    }
  };

  const handleDiscountApplied = (discountInfo: DiscountValidationResponse | null, code: string) => {
    setAppliedDiscount(discountInfo);
    setAppliedDiscountCode(code);
  };

  const checkEmailExists = async (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return;

    setIsCheckingEmail(true);
    try {
      const response = await authService.checkEmailExists(email);
      if (response.success && response.data.exists) {
        setEnrollmentMode('existing');
        setInfoMessage("Welcome back! Please enter your password to continue.");
        setErrors(prev => ({ ...prev, email: '' }));
      } else {
        setEnrollmentMode('new');
        setInfoMessage('');
      }
    } catch (error) {
      console.error('Error checking email:', error);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleEmailBlur = async () => {
    // Track email field completion
    trackForm.fieldComplete('digital-product-checkout', 'email');

    // Check if email exists (only if not authenticated)
    if (enrollmentMode !== 'authenticated') {
      await checkEmailExists(formData.email);
    }

    // Only stash info if we have all required fields filled with valid data
    const hasFirstName = formData.firstName.trim().length > 0;
    const hasLastName = formData.lastName.trim().length > 0;
    const hasValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);

    if (hasFirstName && hasLastName && hasValidEmail && productId && productType) {
      try {
        // Stash the checkout information for abandoned cart follow-up
        await stashedInfoService.createStashedInfo({
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          email: formData.email.trim(),
          product_id: productId,
          product_type: 'digital_product'
        });
        // Silently succeed - no need to show notification to user
      } catch (error) {
        // Silently fail - this is not critical to the checkout process
        console.error('Failed to stash checkout info:', error);
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CheckoutFormData | 'card' | 'terms', string>> = {};

    if (enrollmentMode !== 'authenticated') {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
    }

    if (enrollmentMode === 'new') {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';

      if (!formData.username.trim()) {
        newErrors.username = 'Username is required';
      } else if (formData.username.trim().length < 3) {
        newErrors.username = 'Username must be at least 3 characters';
      } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
        newErrors.username = 'Username can only contain letters, numbers, underscores, and hyphens';
      } else if (errors.username === 'Username is already taken') {
        newErrors.username = 'Username is already taken';
      }
    }

    if (enrollmentMode !== 'authenticated') {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (enrollmentMode === 'new' && formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }

      if (enrollmentMode === 'new' && formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    if (!acceptedTerms) {
      newErrors.terms = 'You must accept the Terms of Service to continue';
    }

    if (!cardComplete) {
      newErrors.card = 'Please enter complete card information';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setErrors(prev => ({
        ...prev,
        card: 'Payment system unavailable. Please disable any ad blockers and refresh the page.'
      }));
      return;
    }

    if (!validateForm()) return;

    setIsProcessing(true);

    const displayPrice = productInfo?.discount_price || productInfo?.full_price || 0;
    trackEnrollment.paymentStart(displayPrice, 'digital_product', productId || '');

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found');

      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
        },
      });

      if (stripeError) {
        trackEnrollment.paymentFailure(stripeError.message || 'Card validation failed', stripeError.code || 'stripe_error', displayPrice, productId || '');
        setErrors(prev => ({ ...prev, card: stripeError.message || 'Card validation failed' }));
        setIsProcessing(false);
        return;
      }

      let response;

      if (enrollmentMode === 'authenticated') {
        response = await enrollmentService.createAuthenticatedEnrollment({
          product_id: productId,
          product_type: 'digital_product',
          payment_method_id: paymentMethod?.id || '',
          discount_code: appliedDiscountCode || undefined,
        });
      } else {
        const enrollmentData: any = {
          email: formData.email,
          password: formData.password,
          product_id: productId,
          product_type: 'digital_product',
          payment_method_id: paymentMethod?.id || '',
          login_mode: enrollmentMode === 'existing',
          discount_code: appliedDiscountCode || undefined,
        };

        if (enrollmentMode === 'new') {
          enrollmentData.username = formData.username;
          enrollmentData.first_name = formData.firstName;
          enrollmentData.last_name = formData.lastName;
        }

        response = await enrollmentService.createEnrollment(enrollmentData);
      }

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Purchase failed');
      }

      trackEnrollment.paymentSuccess(response.data.enrollment?.id || 'unknown', displayPrice, 'digital_product', productId || '');
      
      // Store purchase data for Meta Pixel tracking on welcome page
      sessionStorage.setItem('metaPixelPurchase', JSON.stringify({
        value: displayPrice,
        currency: 'USD',
        content_ids: [productId || ''],
        content_name: productInfo?.title || 'Digital Product',
        content_type: 'product',
        num_items: 1,
      }));

      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
      }

      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        dispatch(userActions.login(response.data.user) as any);
      }

      dispatch(clearEnrollmentProduct());

      setTimeout(() => {
        navigate('/welcome', { replace: true });
      }, 100);

    } catch (error: any) {
      console.error('Checkout error:', error);
      const errorMessage = error.message || 'Purchase failed. Please try again.';
      trackEnrollment.paymentFailure(errorMessage, 'checkout_error', displayPrice, productId || '');
      setErrors(prev => ({ ...prev, card: errorMessage }));
    } finally {
      setIsProcessing(false);
    }
  };

  if (loadingProduct) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-amber-950 to-orange-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300">Loading product information...</p>
        </div>
      </div>
    );
  }

  if (productError || !productInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-amber-950 to-orange-950 flex items-center justify-center p-4">
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-8 max-w-md text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-xl font-semibold text-red-300 mb-2">Product Not Found</h2>
          <p className="text-gray-400 mb-6">{productError}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  const originalPrice = productInfo.full_price;
  const displayPrice = appliedDiscount?.valid
    ? appliedDiscount.final_price
    : productInfo.discount_price || productInfo.full_price;
  const hasDiscount = appliedDiscount?.valid || (productInfo.discount_price && productInfo.discount_price < productInfo.full_price);

  return (
    <>
      <Helmet>
        <title>Complete Your Purchase - AI Mastery Hub</title>
        <meta name="description" content="Complete your digital product purchase. Secure checkout with instant download access." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-black via-amber-950 to-orange-950 py-6 sm:py-12 px-3 sm:px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400 mb-2">
              Complete Your Purchase
            </h1>
            <p className="text-sm sm:text-base text-gray-400">Instant download access after payment</p>
          </div>

          {/* Product Summary */}
          <div className="bg-slate-900/80 backdrop-blur-sm border border-orange-500/20 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 shadow-xl">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <Download className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-200">{productInfo.title}</h2>
                  <p className="text-sm text-gray-400">Digital Download</p>
                </div>
              </div>
              <div className="text-left sm:text-right">
                <div className="text-2xl sm:text-3xl font-bold text-orange-400">{formatPrice(displayPrice)}</div>
                {hasDiscount && (
                  <div className="text-sm text-gray-500 line-through">{formatPrice(originalPrice)}</div>
                )}
              </div>
            </div>

            {/* What's included */}
            <div className="mt-4 pt-4 border-t border-gray-800">
              <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Instant Access</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Lifetime Updates</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Secure Payment</span>
                </div>
              </div>
            </div>
          </div>

          {/* Checkout Form */}
          <form onSubmit={handleSubmit} className="bg-slate-900/80 backdrop-blur-sm border border-orange-500/20 rounded-xl p-4 sm:p-8 shadow-2xl space-y-6 sm:space-y-8">
            {infoMessage && (
              <div className={`rounded-lg p-4 border-l-4 ${
                enrollmentMode === 'authenticated'
                  ? 'bg-emerald-900/20 border-emerald-500'
                  : 'bg-orange-900/20 border-orange-500'
              }`}>
                <p className="text-sm text-gray-300">{infoMessage}</p>
              </div>
            )}

            {/* Personal Information */}
            <div className="space-y-4 sm:space-y-5">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-200 pb-2 sm:pb-3 border-b-2 border-orange-500/30">
                Your Information
              </h2>

              {enrollmentMode === 'new' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="firstName" className="text-sm font-semibold text-gray-300">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleFieldBlur}
                      className={`px-4 py-3 text-gray-200 bg-slate-800/60 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-orange-500/30 ${
                        errors.firstName ? 'border-red-400' : 'border-orange-500/20 focus:border-orange-500'
                      }`}
                      autoComplete="given-name"
                    />
                    {errors.firstName && <span className="text-xs text-red-400">{errors.firstName}</span>}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="lastName" className="text-sm font-semibold text-gray-300">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleFieldBlur}
                      className={`px-4 py-3 text-gray-200 bg-slate-800/60 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-orange-500/30 ${
                        errors.lastName ? 'border-red-400' : 'border-orange-500/20 focus:border-orange-500'
                      }`}
                      autoComplete="family-name"
                    />
                    {errors.lastName && <span className="text-xs text-red-400">{errors.lastName}</span>}
                  </div>
                </div>
              )}

              {enrollmentMode !== 'authenticated' && (
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-sm font-semibold text-gray-300">Email Address</label>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleEmailBlur}
                      className={`w-full px-4 py-3 text-gray-200 bg-slate-800/60 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-orange-500/30 ${
                        errors.email ? 'border-red-400' : 'border-orange-500/20 focus:border-orange-500'
                      }`}
                      autoComplete="email"
                    />
                    {isCheckingEmail && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-5 h-5 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                  {errors.email && <span className="text-xs text-red-400">{errors.email}</span>}
                </div>
              )}

              {enrollmentMode === 'new' && (
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="username" className="text-sm font-semibold text-gray-300">Username</label>
                  <div className="relative">
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleFieldBlur}
                      className={`w-full px-4 py-3 text-gray-200 bg-slate-800/60 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-orange-500/30 ${
                        errors.username ? 'border-red-400' : 'border-orange-500/20 focus:border-orange-500'
                      }`}
                      autoComplete="username"
                      placeholder="Choose a username"
                    />
                    {checkingUsername && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-5 h-5 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                  {errors.username && <span className="text-xs text-red-400">{errors.username}</span>}
                  {!errors.username && formData.username.length >= 3 && !checkingUsername && (
                    <span className="text-xs text-emerald-400">Username is available</span>
                  )}
                </div>
              )}
            </div>

            {/* Password */}
            {enrollmentMode !== 'authenticated' && (
              <div className="space-y-4 sm:space-y-5">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-200 pb-2 sm:pb-3 border-b-2 border-orange-500/30">
                  {enrollmentMode === 'existing' ? 'Enter Password' : 'Create Password'}
                </h2>

                <div className={`grid grid-cols-1 ${enrollmentMode === 'new' ? 'md:grid-cols-2' : ''} gap-4`}>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="password" className="text-sm font-semibold text-gray-300">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleFieldBlur}
                        className={`w-full px-4 py-3 pr-12 text-gray-200 bg-slate-800/60 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-orange-500/30 ${
                          errors.password ? 'border-red-400' : 'border-orange-500/20 focus:border-orange-500'
                        }`}
                        autoComplete={enrollmentMode === 'existing' ? 'current-password' : 'new-password'}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors p-1"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                    {errors.password && <span className="text-xs text-red-400">{errors.password}</span>}
                  </div>

                  {enrollmentMode === 'new' && (
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="confirmPassword" className="text-sm font-semibold text-gray-300">Confirm Password</label>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleFieldBlur}
                        className={`w-full px-4 py-3 text-gray-200 bg-slate-800/60 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-orange-500/30 ${
                          errors.confirmPassword ? 'border-red-400' : 'border-orange-500/20 focus:border-orange-500'
                        }`}
                        autoComplete="new-password"
                      />
                      {errors.confirmPassword && <span className="text-xs text-red-400">{errors.confirmPassword}</span>}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment */}
            <div className="space-y-4 sm:space-y-5">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-200 pb-2 sm:pb-3 border-b-2 border-orange-500/30">
                Payment Information
              </h2>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-300">Card Details</label>
                <div className={`px-4 py-4 min-h-[52px] bg-slate-800/60 border-2 rounded-lg transition-all ${
                  errors.card ? 'border-red-400' : 'border-orange-500/20 focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-500/30'
                }`}>
                  <CardElement options={CARD_ELEMENT_OPTIONS} onChange={handleCardChange} />
                </div>
                {errors.card && <span className="text-xs text-red-400">{errors.card}</span>}
                <p className="text-xs text-gray-500 mt-1">Powered by Stripe. Your payment is secure.</p>
              </div>

              {productId && (
                <DiscountCodeInput
                  productId={productId}
                  productType="digital_product"
                  originalPrice={originalPrice}
                  onDiscountApplied={handleDiscountApplied}
                />
              )}
            </div>

            {/* Terms of Service Checkbox */}
            <div className="mt-6">
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => {
                      setAcceptedTerms(e.target.checked);
                      if (e.target.checked && errors.terms) {
                        setErrors(prev => ({ ...prev, terms: '' }));
                      }
                    }}
                    className="sr-only peer"
                  />
                  <div className={"w-5 h-5 border-2 rounded transition-all flex items-center justify-center " + (errors.terms ? 'border-red-400 bg-red-900/20' : 'border-amber-500/40 bg-slate-800/60 peer-checked:bg-amber-600 peer-checked:border-amber-600 group-hover:border-amber-500/60')}>
                    {acceptedTerms && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-sm text-gray-300 leading-relaxed">
                  I have read and agree to the{' '}
                  <a
                    href="/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-400 hover:text-amber-300 underline transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </a>
                </span>
              </label>
              {errors.terms && <span className="text-xs text-red-400 mt-1 block">{errors.terms}</span>}
            </div>

            {/* Submit */}
            <div className="flex flex-col gap-3 sm:gap-4 mt-4">
              <button
                type="submit"
                className="w-full px-6 sm:px-8 py-4 text-sm sm:text-base font-semibold text-white bg-gradient-to-r from-amber-600 to-orange-600 rounded-lg hover:-translate-y-0.5 hover:shadow-lg hover:shadow-orange-500/50 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed min-h-[52px]"
                disabled={isProcessing || !stripe}
              >
                {isProcessing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Complete Purchase - {formatPrice(displayPrice)}
                  </>
                )}
              </button>

              <p className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-500">
                <Shield className="w-4 h-4" />
                Secure payment powered by Stripe
              </p>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

const DigitalProductCheckoutPage: React.FC = () => {
  const elementsOptions: StripeElementsOptions = {
    appearance: {
      theme: 'night',
    },
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <DigitalProductCheckoutForm />
    </Elements>
  );
};

export default DigitalProductCheckoutPage;
